import { BookOpenIcon } from '@heroicons/react/24/outline';

import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { usePageContext } from '@/renderer/utils/usePageContext';

export default UserGuidePage;

export function UserGuide() {
  const {
    config: { title },
  } = usePageContext();

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="User Guide" Icon={BookOpenIcon} className="py-2 sm:hidden" />
      <div className="overflow-auto">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto hidden max-w-2xl pb-24 pt-20 text-center sm:block">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-200 sm:text-4xl">
              {title}
            </h1>
            <p className="mt-4 text-base leading-7 text-zinc-600 dark:text-zinc-400">
              Last updated on August 28, 2023
            </p>
          </div>
        </div>
        <div className="relative mt-8 px-4 sm:mt-0 sm:px-6 lg:px-8">
          <div className="prose prose-sm prose-zinc mx-auto max-w-prose dark:prose-invert prose-a:font-semibold prose-a:text-emerald-500 hover:prose-a:text-emerald-600">
            <h3>Beating line movement</h3>
            <h4>Optimize Your Setup</h4>
            <p>
              We recommend using a desktop when possible and having two or more devices ready to get
              releases and place bets efficiently.
            </p>
            <p>Every split second counts. Be well prepared to act quickly.</p>
            <p>
              There are little things you can do to make the process faster. Copy and paste bet
              amounts, have passwords saved in your browser, use the "Find" (Control-F) feature to
              locate game numbers or team names on the screen, everything helps.
            </p>
            <h4>Derivatives and Alt-Lines</h4>
            <p>
              When available, 1st half lines, money lines and alt-lines will often not move as
              quickly as traditional full game prices but these bets typically have comparable +EV
              over the long run.
            </p>
            <h4>Choosing a Sportsbook</h4>
            <p>
              Sportsbook selection may be the biggest factor in determining whether or not you can
              get release lines on a consistent basis. Some sportsbooks move lines faster than
              others, and some have much more intense competition for lines than others. You will
              likely have to do some experimenting with different outs to find a workable and
              sustainable solution.
            </p>
            <p>
              Please note that we will not be able to recommend any specific sportsbooks. We get
              asked this question very frequently. If we were to send subscribers to a specific
              sportsbook, it would likely no longer work well for everyone.
            </p>
            <h3>Frequently asked questions</h3>
            <h4>I missed the release or wasn't able to get the release line, what do I do?</h4>
            <p>
              When this happens it is almost always best to just pass the game. You don't want to
              take a bad price, you don't want to buy points, and you don't want to pay extra juice
              to get closer to the release line. In the rare case that a line returns to our release
              line at some point in the market, there often could be new information, or it likely
              isn't going to be a high performing subset of plays for us. You can always message us
              and ask for an updated opinion.
            </p>
            <h4>
              What can I do to lower the chances of getting my account banned or limited from
              playing RAS releases?
            </h4>
            <p>
              Mix up your action, don't play only RAS releases in any one account. Spread out your
              action. If we have 5 plays, ideally you would bet 1 release each in 5 different
              accounts. We realize this is not always realistic, but you get the idea. Don't bet the
              max. If your max is $1000, choose a lower amount. No matter what you do, once you win
              enough from any sportsbook you are likely to get limited or banned, so be proactive
              and have backup accounts ready to go.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

// Main component
function UserGuidePage() {
  return <UserGuide />;
}
